import { type DocumentChunk, type DocumentChunkMapByFilename, DocumentChunkManifest } from '../types/chunkedDocuments';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../utils/exceptionHandler.platform';
import makeLogger from '../utils/makeLogger';
import backgroundPortalGate from './portalGates/toBackground';
import { createDataUrl } from './utils/createDataUrl';
import { unpackChunk } from './utils/unpackChunk';

export type DataUrlsByAbsolutePath = { [absolutePath: string]: string; };

const logger = makeLogger(__filename);

async function processImageChunk(
  chunk: DocumentChunk,
  absolutePath: string,
  dataUrlsByAbsolutePath: DataUrlsByAbsolutePath,
  docId: string,
): Promise<void> {
  try {
    const fileContent = await unpackChunk(chunk, docId);
    if (fileContent?.data) {
      const dataUrl = createDataUrl(absolutePath, fileContent.data);
      dataUrlsByAbsolutePath[absolutePath] = dataUrl;
    } else {
      logger.warn('No image data unpacked for chunk in foreground', { absolutePath, chunkId: chunk.internal_id, chunkType: chunk.type, hasFileContent: Boolean(fileContent) });
    }
  } catch (error) {
    exceptionHandler.captureException('Error processing image chunk in foreground', {
      extra: { absolutePath, chunkId: chunk.internal_id, error },
    });
  }
}

/**
 * Fetches raw image chunks from the background, then unpacks, decrypts (if necessary),
 * and converts them to data URLs in the foreground.
 *
 * @param manifest The document chunk manifest.
 * @param absoluteFilepaths An array of absolute file paths for the images to load.
 * @returns A promise that resolves to a map of absolute file paths to their data URL strings.
 */
export async function loadImageDataUrls(
  manifest: DocumentChunkManifest,
  absoluteFilepaths: string[],
  docId: string,
): Promise<DataUrlsByAbsolutePath | null> {
  logger.debug(`Requesting ${absoluteFilepaths.length} raw image chunks from background.`);
  if (absoluteFilepaths.length === 0) {
    return null;
  }

  let imageChunksByFilename: DocumentChunkMapByFilename = {};
  try {
    imageChunksByFilename = await backgroundPortalGate.loadChunksByFilenameFromManifest(
      manifest,
      absoluteFilepaths,
      docId,
    );
  } catch (error) {
    exceptionHandler.captureException('Error fetching raw image chunks via portal gate', {
      extra: {
        numFiles: absoluteFilepaths.length,
        error,
      },
    });
    return null;
  }
  const dataUrlsByAbsolutePath: DataUrlsByAbsolutePath = {};
  const processingPromises: Promise<void>[] = [];

  for (const absolutePath of absoluteFilepaths) {
    const chunk = imageChunksByFilename[absolutePath];
    if (!chunk) {
      exceptionHandler.captureException('Chunk not found in map returned from background for path:', {
        extra: {
          absolutePath,
          imageChunksByFilename,
          manifest,
        },
      });
      continue;
    }

    processingPromises.push(processImageChunk(chunk, absolutePath, dataUrlsByAbsolutePath, docId));
  }

  await Promise.all(processingPromises);

  logger.debug('Foreground image processing completed', {
    requestedFiles: absoluteFilepaths.length,
    dataUrlsCreated: Object.keys(dataUrlsByAbsolutePath).length,
  });
  return dataUrlsByAbsolutePath;
}
